import * as JSZip from "jszip";
import { IImageInFile } from "src/assets/libs/office-open-xml-js/interfaces";
import { MainConfig } from "src/config/main.config";
import { IImageWrapper } from "src/models/image-wrapper";
import {
  FileHandlerAbstract,
  FileHandlerActionEnum,
  IAddImageFromUrlParameters,
  IFileHanderParameters,
  IFileHandlerAddSourceAction,
  IFileHandlerExcludeImageFromLicense,
  IFileHandlerPublishAsOerAction,
  IFileHandlerReplaceImageAction,
  ISaveToFileProgress,
} from "./file-handler.abstract";
import { MimeType, DataUrl, UUID } from "../utils/utils";
import * as Base64Arraybuffer from "base64-arraybuffer";
import * as Tiff from "browser-tiff.js";
import { License } from "src/assets/libs/office-open-xml-js/license";
import { Subject } from "rxjs";
import { JsZipHelper } from "./js-zip-helper";
import {
  ISource,
  IReference,
} from "src/assets/libs/office-open-xml-presentation-js/interfaces";
import { LicensesHumanReadable, LicensesBaseHref } from "src/models/licenses";
import { Crypto } from "../utils/utils";
import { AddImageAsBase64Parameters } from "../assets/libs/office-open-xml-js/interfaces";
import { v4 as UUIDv4 } from "uuid";
import { Document as DocumentModel } from "../models/document.model";

export abstract class OpenDocumentHandler extends FileHandlerAbstract {
  protected _zip: JSZip;

  constructor(params: IFileHanderParameters) {
    super(params);
    let _zipPromise = JsZipHelper.getContent(this._file.file);
    _zipPromise.then(async (zip) => {
      this._zip = zip;
      const mimeType = new MimeType(
        "application/vnd.oasis.opendocument.presentation"
      );
      const guid = await this.getGuid();
      this._document = new DocumentModel(guid, mimeType);
      this._subjectSubhandlerReady.next();
    });
  }

  public async resetFileState(){
    this._zip  = await  JsZipHelper.getContent(this._file.file);
    this.additionalDisclaimerText = ""
  }

  public async getImages(): Promise<IImageWrapper[]> {
    await this._zip;
    const _images: IImageWrapper[] = [];
    const _imagesLoading: Promise<void>[] = [];

    for (const imageFromFile of await this.getImagesFromZip()) {
      _imagesLoading.push(
        new Promise<void>((resolve) => {
          const tmpImg = new Image();
          tmpImg.src = imageFromFile.dataBase64.toString();
          tmpImg.onload = () => {
            _images.push({
              internalId: imageFromFile.id,
              base64: imageFromFile.dataBase64,
              source: imageFromFile.path,
              dimension: {
                width: tmpImg.width,
                height: tmpImg.height,
                ratio: tmpImg.width / tmpImg.height,
              },
            });
            resolve();
          };
        })
      );
    }
    await Promise.all(_imagesLoading);
    return _images;
  }

  protected async getImagesFromZip() {
    const pathStart = "Pictures/";
    const _images: IImageInFile[] = [];
    const _imagesLoading: Promise<void>[] = [];
    const unsupportedImages : string[] = [];
    this._zip.folder(pathStart).forEach((relativePath: string) => {
      _imagesLoading.push(
        new Promise<void>(async (resolve) => {
          const fileName: string = relativePath;
          const fileEnding: string = fileName.split(".")[1];
          // check if this type of media is supported by the tool
          let _isIncluded = false;
          for (const type of MainConfig.supportedMediaFormats.images) {
            if (type === fileEnding) {
              _isIncluded = true;
              break;
            }
          }
        // if file is not supported, then don't process its data
        if (!_isIncluded) {
          console.log("Skipping unsupported image file", relativePath, fileEnding);
          unsupportedImages.push(fileName);
          return resolve();
        }


          let mimeType: MimeType;
          let dataBase64;
          const fullPath = pathStart + fileName;

          if (fileEnding === "tiff") {
            const base64 = await this._zip.file(fullPath).async("base64");
            const arrayBuffer = Base64Arraybuffer.decode(base64);
            mimeType = MimeType.fromFileExtension("png");
            try {
              const tiff = new Tiff({ buffer: arrayBuffer });
              // Tiff js converts tiff file client-side to png
              dataBase64 = tiff.toDataURL();
              mimeType = MimeType.fromFileExtension("png");
            } catch (error) {
              console.log("Error converting TIFF file", error);
              dataBase64 = new DataUrl(
                mimeType,
                await this._zip.file(fullPath).async("base64"),
                true
              );
            }
          } else {
            mimeType = MimeType.fromFileExtension(fileEnding);
            dataBase64 = new DataUrl(
              mimeType,
              await this._zip.file(fullPath).async("base64"),
              true
            );
          }
          _images.push({
            dataBase64: new DataUrl(
              mimeType,
              await this._zip.file(fullPath).async("base64"),
              true
            ),
            path: fullPath,
            id: Number(fileName.match(/\d+/g)),
          });
          resolve();
        })
      );
    });
    if(unsupportedImages.length > 0){
      alert(`Some files are not supported by this tool and will be skipped!\nThey can be replaced later manually after downloading the file.\n\nEinige Bilder in dieser Datei werden nicht unterstützt und müssen später manuell selbst ersetzt werden.\n\n\nUnsupported files: ${unsupportedImages.join(', ')}`);
    }
    await Promise.all(_imagesLoading);
    return _images;
  }

  public async saveToFile(
    targetLicense: License,
    logSubject?: Subject<ISaveToFileProgress>
  ): Promise<void> {
    await this._executeActionQueue(logSubject);
    await this.addDisclaimer(this.disclaimerText + this.additionalDisclaimerText, targetLicense);
    return JsZipHelper.zipToFile(this._zip, {
      fileName: 'OER_'+ this._file.file.name,
      progressSubject: logSubject,
    }).then((result) => {
      logSubject.next({
        action: FileHandlerActionEnum.READY_TO_DOWNLOAD,
        text: "Done!",
      });
      return result;
    });
  }

  public async addDisclaimer(
    disclaimerText: string,
    targetLicense: License
  ): Promise<void> {
    return await this.addLicensingElement(disclaimerText, targetLicense);
  }

  public abstract addLicensingElement(
    legalText: string,
    targetLicense: License
  ): Promise<void>;

  public async _executeActionQueue(
    logSubject?: Subject<ISaveToFileProgress>
  ): Promise<void> {
    // check for actions that demand to create a source on a slide. Put them all in a second queue for now and process them once all images
    // were replaced
    let excludedFirstImageWithMention : boolean = false;
    let _totalNumberOfActions = this._actionQueue.length;
    const _addSourceQueue: IFileHandlerAddSourceAction[] = [];
    const _logPrefix = (currentNumber: number, totalNumber: number) =>
    `Executing instruction ${currentNumber} / ${totalNumber}: `;
    let _counterCurrentAction = 0;
    const actionQueue = [...this._actionQueue];
    while (actionQueue.length > 0) {
      _counterCurrentAction++;
      let _log = _logPrefix(_counterCurrentAction, _totalNumberOfActions);
      const _action = actionQueue.shift();
      switch (_action.action) {
        case FileHandlerActionEnum.REPLACE_IMAGE_BY_URL: {
          const _actReplace = _action as IFileHandlerReplaceImageAction;
          const _dataUrlReplacingImage: DataUrl = await DataUrl.fromImageUrl(
            _actReplace.replacingBy.source
          );
          (await this._receipt).addMediumReplacedAction({
            doNotUpload: _actReplace.doNotUpload,
            wasReplaced: {
              internalPath: _actReplace.toReplace.source,
              checksum: Crypto.md5(_actReplace.toReplace.base64.toString()),
            },
            isReplacing: {
              width: _actReplace.replacingBy.dimension.width,
              height: _actReplace.replacingBy.dimension.height,
              // TODO: think about setting the MIME type
              checksum: Crypto.md5(_dataUrlReplacingImage.toString()),
              dataUrlBase64: _dataUrlReplacingImage,
              // TODO: the source API targets something like 'cc', it wants to know which of the provided APIs by the
              //  tool is the origin. Fix it.
              sourceApi: this._deriveHostNameFromUrl(
                _actReplace.replacingBy.source
              ),
              sourceUrl: _actReplace.replacingBy.source,
              license: {
                name: LicensesHumanReadable[_actReplace.replacingBy.license],
                version: _actReplace.replacingBy.licenseVersion,
                deedsUrl: _actReplace.replacingBy.licenseUrl,
              },
            },
          });
          const _imageReplacedBy: IImageWrapper = (
            _action as IFileHandlerReplaceImageAction
          ).replacingBy;
          _addSourceQueue.push({
            action: FileHandlerActionEnum.ADD_SOURCE_CREDITS,
            mediaType: "image",
            credits: {
              licenseHumanReadable:
                LicensesHumanReadable[_imageReplacedBy.license],
              author: _imageReplacedBy.author,
              description: _imageReplacedBy.description,
              originUrl: _imageReplacedBy.source,
              licenseVersion: _imageReplacedBy.licenseVersion,
              // todo get author url
              authorUrl: undefined,
              title: _actReplace.replacingBy.title,
            },
            filePath: (_action as IFileHandlerReplaceImageAction).toReplace
              .source,
          });
          // increment the total number of actions by 1 for each newly spawned ADD_SOURCE_CREDITS action
          _totalNumberOfActions++;
          _log += `Replacing Image`;
          await this.addImageFromUrl({
            nameAndFullPath: (_action as IFileHandlerReplaceImageAction)
              .toReplace.source,
            urlToImage: (_action as IFileHandlerReplaceImageAction).replacingBy
              .source,
            dimensions: {
              width: (_action as IFileHandlerReplaceImageAction).toReplace
                .dimension.width,
              height: (_action as IFileHandlerReplaceImageAction).toReplace
                .dimension.height,
            },
          });

          if (logSubject) {
            logSubject.next({
              action: FileHandlerActionEnum.REPLACE_IMAGE_BY_URL,
              text: _log,
              current: _counterCurrentAction,
              total: _totalNumberOfActions,
            });
          }
          break;
          // TODO: make sure it can expect an `edited` field (so far only edited ones use the AsBase64 functionality, but there is no
          //  safety-check
        }
        case FileHandlerActionEnum.REPLACE_IMAGE_BY_BASE64: {
          const _actReplaceByBase64 = _action as IFileHandlerReplaceImageAction;
          const _imageReplacedByBase64: IImageWrapper = (
            _action as IFileHandlerReplaceImageAction
          ).replacingBy;
          // Replacing by image from machine uses the same procedure, but then it obviously does not have a real URL.
          // Instead, use the base64 field which resembles the original image before editing
          const _dataUrlReplacingImageOriginalBase64: any =
            _actReplaceByBase64.replacingBy.source
              ? await DataUrl.fromImageUrl(
                  _actReplaceByBase64.replacingBy.source
                )
              : _actReplaceByBase64.replacingBy.base64;
          const _dataUrlReplacingImageEditedBase64: any =
            _actReplaceByBase64.replacingBy.edited.base64;
          (await this._receipt).addMediumReplacedAction({
            doNotUpload: _actReplaceByBase64.doNotUpload,
            wasReplaced: {
              internalPath: _actReplaceByBase64.toReplace.source,
              checksum: Crypto.md5(_actReplaceByBase64.toReplace.base64),
            },
            isReplacing: {
              width: _actReplaceByBase64.replacingBy.dimension.width,
              height: _actReplaceByBase64.replacingBy.dimension.height,
              // TODO: think about setting the MIME type
              checksum: Crypto.md5(
                _dataUrlReplacingImageOriginalBase64.toString()
              ),
              dataUrlBase64: _dataUrlReplacingImageOriginalBase64,
              // TODO: the source API targets something like 'cc', it wants to know which of the provided APIs by the
              //  tool is the origin. Fix it.
              sourceApi: this._deriveHostNameFromUrl(
                _actReplaceByBase64.replacingBy.source
              ),
              sourceUrl: _actReplaceByBase64.replacingBy.source,
              authorUserName: _actReplaceByBase64.replacingBy.edited.editor,
              license: {
                name: LicensesHumanReadable[
                  _actReplaceByBase64.replacingBy.license
                ],
                version: _actReplaceByBase64.replacingBy.licenseVersion,
                deedsUrl: _actReplaceByBase64.replacingBy.licenseUrl,
              },
              edited: {
                width: _actReplaceByBase64.replacingBy.edited.dimension.width,
                height: _actReplaceByBase64.replacingBy.edited.dimension.height,
                checksum: Crypto.md5(
                  _dataUrlReplacingImageEditedBase64.toString()
                ),
                dataUrlBase64: _dataUrlReplacingImageEditedBase64,
              },
            },
          });
          _addSourceQueue.push({
            action: FileHandlerActionEnum.ADD_SOURCE_CREDITS,
            mediaType: "image",
            credits: {
              licenseHumanReadable:
                LicensesHumanReadable[_imageReplacedByBase64.license],
              author: _imageReplacedByBase64.author,
              description: _imageReplacedByBase64.description,
              originUrl: _imageReplacedByBase64.source,
              licenseVersion: _imageReplacedByBase64.licenseVersion,
              // todo get author url
              authorUrl: undefined,
              title: _actReplaceByBase64.replacingBy.title,
            },
            filePath: _actReplaceByBase64.toReplace.source,
            // if info about edit is available, add it to the source info, otherwise set it to `undefined`
            edited: _actReplaceByBase64.replacingBy.edited
              ? {
                  editor: _actReplaceByBase64.replacingBy.edited.editor,
                  actions: _actReplaceByBase64.replacingBy.edited.action,
                }
              : undefined,
          });
          // increment the total number of actions by 1 for each newly spawned ADD_SOURCE_CREDITS action
          _totalNumberOfActions++;
          _log += "Replacing Image";
          await this.addBase64Image({
            nameAndFullPath: (_action as IFileHandlerReplaceImageAction)
              .toReplace.source,
            imageAsBase64: (
              _action as IFileHandlerReplaceImageAction
            ).replacingBy.edited.base64.getData(),
          });
          if (logSubject) {
            logSubject.next({
              action: FileHandlerActionEnum.REPLACE_IMAGE_BY_BASE64,
              text: _log,
              current: _counterCurrentAction,
              total: _totalNumberOfActions,
            });
          }
          break;
        }
        case FileHandlerActionEnum.ADD_SOURCE_CREDITS:
          _addSourceQueue.push(_action as IFileHandlerAddSourceAction);
          break;
        case FileHandlerActionEnum.PUBLISH_AS_OER: {
          const _actOer: IFileHandlerPublishAsOerAction =
            _action as IFileHandlerPublishAsOerAction;
          const _madeOerDataUrl: DataUrl = DataUrl.fromString(
            _actOer.image.base64.toString()
          );
          (await this._receipt).addImagePublishedAsOerAction({
            doNotUpload: _actOer.doNotUpload,
            internalPath: _actOer.image.source,
            title: _actOer.title,
            checksum: Crypto.md5(_madeOerDataUrl.toString()),
            width: _actOer.image.dimension.width,
            height: _actOer.image.dimension.height,
            author: {
              name: _actOer.author,
              url: _actOer.authorUrl,
              affiliation: _actOer.affiliation,
            },
            license: {
              name: LicensesHumanReadable[_actOer.license.enum],
              version: _actOer.license.version,
              deedsUrl:
                LicensesBaseHref[_actOer.license.enum] +
                "/" +
                _actOer.license.version,
            },
            dataUrlBase64: _madeOerDataUrl,
          });
          _addSourceQueue.push({
            filePath: _actOer.image.source,
            mediaType: "image",
            credits: {
              licenseHumanReadable: LicensesHumanReadable[_actOer.license.enum],
              licenseVersion: _actOer.license.version,
              author: _actOer.author,
              description: _actOer.description,
              originUrl: _actOer.originUrl,
              originName: _actOer.originName,
              authorUrl: _actOer.authorUrl,
              title: _actOer.title,
            },
            role: _actOer.role,
            screenshotData: !_actOer.screenshotData
              ? undefined
              : {
                  date: _actOer.screenshotData.date,
                  description: _actOer.screenshotData.description,
                },
          } as IFileHandlerAddSourceAction);
          break;
        }
        case FileHandlerActionEnum.EXCLUDE_FROM_LICENSE:
          const excludeAction = _action as IFileHandlerExcludeImageFromLicense;
          if(!excludeAction.withoutNotice){
            let textToAdd : string = excludedFirstImageWithMention ? ',\n ' : `Excluded are: `
            textToAdd += excludeAction.noticeText;
            excludedFirstImageWithMention = true;
            this.additionalDisclaimerText += textToAdd;
          }
          break;
        default:
          if (logSubject) {
            logSubject.next({
              action: FileHandlerActionEnum.ABORTING_ON_ERROR,
              text: "An error occured. See console or cry for help.",
            });
          }
          console.log(`Tried to execute unknown action in queue: ${_action}`);
          break;
      }
    }

    let _counterRef = 1;
    const _sources: ISource[] = [];
    const _references: IReference[] = [];
    for (const source of _addSourceQueue) {
      const _refAbbr = "" + _counterRef++;
      _sources.push({
        refAbbreviation: _refAbbr,
        hrefLicense: LicensesBaseHref[source.credits.licenseHumanReadable],
        licenseHumanReadable: source.credits.licenseHumanReadable,
        licenseVersion: source.credits.licenseVersion,
        author: source.credits.author,
        description: source.credits.description,
        originUrl: source.credits.originUrl,
        // if an originName was passed, use it. Otherwise derive one from the hostname
        originName: source.credits.originName
          ? source.credits.originName
          : this._deriveHostNameFromUrl(source.credits.originUrl),
        authorUrl: source.credits.authorUrl,
        title: source.credits.title,
        // if no info about an edit was passed, source.edited will be undefined get propagated correctly
        edited: source.edited,
        role: source.role,
        screenshotData: !source.screenshotData
          ? undefined
          : {
              date: source.screenshotData.date,
              description: source.screenshotData.description,
            },
      } as ISource);
      _references.push({ file: source.filePath, refAbbreviation: _refAbbr });
    }
    await this.addBibRefsToImages(_references);

    _counterCurrentAction++;
    if (logSubject) {
      logSubject.next({
        action: FileHandlerActionEnum.ADD_SOURCE_CREDITS,
        text:
          _logPrefix(_counterCurrentAction, _totalNumberOfActions) +
          "Appending source",
        current: _counterCurrentAction,
        total: _totalNumberOfActions,
      });
    }
    await this.addSources(_sources);

    if(excludedFirstImageWithMention){
      this.additionalDisclaimerText += "\n.The rights are reserved by the respective owners."
    }
  }

  public async getFileAuthorName(): Promise<string> {
    let author : string = 'unknown';
    const _coreXmlContent = await this._zip.file(`meta.xml`).async("text");
    const _coreXmlDoc = new DOMParser().parseFromString(
      _coreXmlContent,
      "application/xml"
    );
    const dc_creator = _coreXmlDoc.getElementsByTagName("dc:creator");

    const meta_initial_creator = _coreXmlDoc.getElementsByTagName("meta:initial-creator");
    if(dc_creator != null && dc_creator.length > 0){
        author =  dc_creator[0].innerHTML;
    }else if(meta_initial_creator != null && meta_initial_creator.length > 0){
        author =  meta_initial_creator[0].innerHTML;
    }
    return author;
  }

  public addImageFromUrl(image: IAddImageFromUrlParameters): Promise<void> {
    return this._addImageFromUrl(image);
  }

  protected async _addImageFromUrl(
    image: IAddImageFromUrlParameters
  ): Promise<void> {
    // check if no error occured by checking if the filePath is correct
    const _imgPath = image.nameAndFullPath;
    if (Object.prototype.hasOwnProperty.call(this._zip.files, _imgPath)) {
      const _pathArray = _imgPath.split("/");
      // the slice thingy puts only the last element (which would be the file name) in a new array and then calling
      // the zeroest element makes it a string, such that the split method can be used
      const _fileNameArray = _pathArray.slice(-1)[0].split(".");
      // since _fileNameArray will always have [0]: file name and [1]: file ending, it is safe to use it this way
      const _fileEndingOfTheImageFileThatIsToBeReplaced = _fileNameArray[1];
      // const _base64ImagePrefix = `data:image/${_fileEndingOfTheImageFileThatIsToBeReplaced};base64,`;
      // const _pureBase64 = this._selectedImageFromFile.base64

      /**
       * big props and credits to
       * Balvinder Singh, https://medium.com/@erbalvindersingh/fetch-an-image-from-url-in-angular-4-and-store-locally-4928d3b504fc
       **/

      // creating a new image, load the external image into that img
      // Then create a canvas, load the image into the canvas and convert the canvas into a base64 string
      let img = new Image();
      img.crossOrigin = "anonymous";
      img.src = image.urlToImage;

      return new Promise((resolve, reject) => {
        img.onload = () => {
          /**
           * Props to alessandrio @ https://stackoverflow.com/a/43809873/7618184
           */

          let canvas = document.createElement("canvas");
          const _actualWidth = img.width;
          const _actualHeight = img.height;
          const _targetWidth = image.dimensions.width;
          const _targetHeight = image.dimensions.height;
          let _scale = Math.min(
            _targetWidth / _actualWidth,
            _targetHeight / _actualHeight
          );
          // make sure an replacing image will only be scaled down. If it is scaled up, it will consume more space but looks just
          // as bad as scaling it on-the-fly by the presentation software, i.e. PowerPoint
          _scale = Math.min(1, _scale);
          const _scaledWidth = _actualWidth * _scale;
          const _scaledHeigth = _actualHeight * _scale;
          canvas.width = _scaledWidth;
          canvas.height = _scaledHeigth;
          let ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, _scaledWidth, _scaledHeigth);

          // [0] will be base64 prefix, something like 'data:image/png;base64', whereas [1] contains the image encoded as base64
          const _base64Image = canvas
            .toDataURL(`image/${_fileEndingOfTheImageFileThatIsToBeReplaced}`)
            .split(",")[1];

          // release the occupied references
          img = null;
          canvas = null;
          ctx = null;

          // thanks to Benjamin Gruenbaum, Patrick Roberts @ https://stackoverflow.com/a/22519785/7618184
          this._addBase64Image({
            nameAndFullPath: _imgPath,
            imageAsBase64: _base64Image,
          })
            .then(() => {
              resolve();
            })
            .catch((error) => {
              reject(error);
            });
        };
      });
    }
  }

  protected async _addBase64Image(
    image: AddImageAsBase64Parameters
  ): Promise<void> {
    this._zip = this._zip.file(image.nameAndFullPath, image.imageAsBase64, {
      base64: true,
    });
  }

  public async addBase64Image(
    image: AddImageAsBase64Parameters
  ): Promise<void> {
    return this._addBase64Image(image);
  }

  /**
   * Adds manifest/meta information for the an image
   * @param filePath The full filepath (incl. folders and file extension) for the image file
   * @param mediaType The media type of the image (defaults to image/png)
   */
  protected async addManifestForImage(
    filePath: string,
    mediaType: string = "image/png"
  ) {
    const _xml = await this._zip.file("META-INF/manifest.xml").async("text");
    const _docAppXml: Document = await new DOMParser().parseFromString(
      _xml,
      "application/xml"
    );
    _docAppXml.querySelector(
      "manifest"
    ).innerHTML += `<manifest:file-entry manifest:full-path="${filePath}" manifest:media-type="${mediaType}"/>`;
    const _xmlSerializer = new XMLSerializer();
    this._zip.file(
      "META-INF/manifest.xml",
      _xmlSerializer.serializeToString(_docAppXml)
    );
  }

  /**
   * If a GUID is present, return it. If no GUID can be found, generate a new one and save it to the file.
   */
  protected async getGuid(): Promise<UUID> {
    const _readGuidString = null; // await this.ooxmlFile.getCustomData(MainConfig.variableNameGuidData);
    let guid: UUID;
    if (_readGuidString == null || !UUID.isUUID(_readGuidString)) {
      const _newGuid: UUID = new UUID(UUIDv4());
      // Save GUID
      // await this.ooxmlFile.addCustomDataAsString(MainConfig.variableNameGuidData, _newGuid.toString());
      guid = _newGuid;
    } else {
      guid = new UUID(_readGuidString);
    }
    return guid;
  }
}
